import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit3, Rocket, BarChart2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENTS = [
  'linear-gradient(135deg, #6C5CE7, #00D9FF)',
  'linear-gradient(135deg, #FF7A59, #FFB84D)',
  'linear-gradient(135deg, #20E3B2, #6C5CE7)',
  'linear-gradient(135deg, #BC13FE, #FF10F0)',
  'linear-gradient(135deg, #00D9FF, #20E3B2)',
  'linear-gradient(135deg, #6C5CE7, #FF7A59)',
];

const STATUS_BADGE = {
  DRAFT: { bg: '#F3F4F6', text: '#6B7280', label: 'Draft' },
  ACTIVE: { bg: '#D1FAE5', text: '#059669', label: 'Active' },
  READY_TO_ACTIVATE: { bg: '#DBEAFE', text: '#2563EB', label: 'Ready to Activate' },
  READY_TO_LAUNCH: { bg: '#FEF3C7', text: '#D97706', label: 'Ready to Launch' },
  LAUNCHED: { bg: '#D1FAE5', text: '#059669', label: 'Launched' },
  LIVE: { bg: '#D1FAE5', text: '#059669', label: 'Live' },
  NEEDS_REVIEW: { bg: '#FEF3C7', text: '#D97706', label: 'Needs Review' },
  PENDING_APPROVAL: { bg: '#FEF3C7', text: '#D97706', label: 'Pending Approval' },
};

const MyPortfoliosPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter');
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchPortfolios(); }, [filter]);

  const fetchPortfolios = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/creator/portfolios`, { headers: { Authorization: `Bearer ${token}` } });
      let filtered = res.data.portfolios || [];
      if (filter === 'active') filtered = filtered.filter(p => ['ACTIVE', 'LAUNCHED', 'LIVE'].includes(p.status));
      setPortfolios(filtered);
    } catch (error) { console.error('Error fetching portfolios:', error); }
    finally { setLoading(false); }
  };

  const getGradient = (id) => {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i);
    return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
  };

  const getCTA = (p) => {
    const draftId = p.contract_id || p.contract_draft_id;
    switch (p.status) {
      case 'DRAFT': case 'draft':
      case 'READY_TO_ACTIVATE': case 'READY_TO_LAUNCH': case 'NEEDS_REVIEW':
        // F-Launch: surface a single "Complete Setup" CTA -> detail page where the
        // Launch Readiness panel enforces required fields + deploy + go-live.
        if (draftId && (p.status === 'READY_TO_ACTIVATE' || p.status === 'READY_TO_LAUNCH')) {
          return {
            label: 'Complete Setup',
            icon: <Edit3 size={13} />,
            action: () => navigate(`/creator/portfolios/${p.id}`),
            disabled: false,
          };
        }
        return {
          label: 'Complete Setup',
          icon: <Edit3 size={13} />,
          action: () => navigate(`/creator/portfolios/${p.id}`),
          disabled: false,
        };
      case 'ACTIVE': case 'LAUNCHED': case 'LIVE':
        return { label: 'View Performance', icon: <BarChart2 size={13} />, action: () => navigate(`/creator/portfolios/${p.id}`), disabled: false };
      default:
        return { label: 'View Details', icon: <BarChart2 size={13} />, action: () => navigate(`/creator/portfolios/${p.id}`), disabled: false };
    }
  };

  return (
    <div data-testid="my-portfolios-page" style={{ padding: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, fontFamily: theme.fonts.heading }}>
            {filter === 'active' ? 'Active Portfolios' : 'My Portfolios'}
          </h1>
          <p style={{ fontSize: '15px', color: theme.colors.text.secondary, marginTop: '4px' }}>
            {portfolios.length} portfolio{portfolios.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button data-testid="create-portfolio-btn" onClick={() => navigate('/creator/portfolios/new')}
          style={{ background: theme.colors.primary, color: '#FFF' }}>
          <Plus size={16} /> Create Portfolio
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: theme.colors.text.muted }}>Loading portfolios...</div>
      ) : portfolios.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px', background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.card }}>
          <p style={{ color: theme.colors.text.muted, fontSize: '15px' }}>No portfolios found. Create your first portfolio!</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {portfolios.map(p => {
            const badge = STATUS_BADGE[p.status] || STATUS_BADGE.DRAFT;
            const cta = getCTA(p);
            return (
              <div key={p.id} data-testid={`portfolio-card-${p.id}`}
                onClick={() => navigate(`/creator/portfolios/${p.id}`)}
                style={{
                  background: theme.colors.bg.card, borderRadius: theme.radius.card,
                  border: `1px solid ${theme.colors.borders}`, overflow: 'hidden',
                  cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = theme.colors.shadows.lg; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>

                {/* Cover Image */}
                <div style={{
                  width: '100%', height: '140px',
                  background: p.cover_image_url ? `url(${p.cover_image_url}) center/cover` : getGradient(p.id),
                  position: 'relative',
                }}>
                  <span style={{
                    position: 'absolute', top: '10px', right: '10px',
                    padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                    background: badge.bg, color: badge.text,
                  }}>{badge.label}</span>
                </div>

                {/* Content */}
                <div style={{ padding: '16px 20px 20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '8px' }}>{p.name}</h3>

                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    {p.unit_price > 0 && (
                      <span style={{ fontSize: '12px', color: theme.colors.text.secondary }}>
                        ${p.unit_price}
                      </span>
                    )}
                    {p.quantity > 0 && (
                      <span data-testid={`portfolio-qty-${p.id}`} style={{ fontSize: '12px', color: theme.colors.text.secondary }}>
                        Qty: {p.quantity}
                      </span>
                    )}
                    {p.kickback_percent > 0 && (
                      <span style={{ fontSize: '12px', color: theme.colors.primary, fontWeight: 600 }}>
                        {p.kickback_percent}% kickback
                      </span>
                    )}
                    {typeof p.utilities_count === 'number' && p.utilities_count >= 0 && (
                      <span data-testid={`portfolio-utilities-${p.id}`} style={{ fontSize: '12px', color: theme.colors.text.muted }}>
                        {p.utilities_count} utilit{p.utilities_count === 1 ? 'y' : 'ies'}
                      </span>
                    )}
                    {p.category && (
                      <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>{p.category}</span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px' }}>
                    <Calendar size={12} color={theme.colors.text.muted} />
                    <span style={{ fontSize: '12px', color: theme.colors.text.muted }}>{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>

                  {/* CTA Button */}
                  <button data-testid={`portfolio-cta-${p.id}`}
                    onClick={(e) => { e.stopPropagation(); cta.action(); }}
                    disabled={cta.disabled}
                    title={cta.tooltip || ''}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      width: '100%', padding: '8px', fontSize: '13px', fontWeight: 600,
                      background: cta.disabled ? `${theme.colors.text.muted}10` : `${theme.colors.primary}10`,
                      color: cta.disabled ? theme.colors.text.muted : theme.colors.primary,
                      border: `1px solid ${cta.disabled ? theme.colors.borders : `${theme.colors.primary}30`}`,
                      borderRadius: theme.radius.sm,
                      cursor: cta.disabled ? 'not-allowed' : 'pointer',
                    }}>
                    {cta.icon} {cta.label}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyPortfoliosPage;
